import { Stack, TextField, FormControl, InputLabel, 
  Select, MenuItem, 
  Switch,
  FormControlLabel,
  Autocomplete} from '@mui/material';
import * as React from 'react';
import * as ATCAConstants from '../util/ATCAConstants';
import * as Planets from '../astro/Planets';
import * as Utils from '../util/Utils';

export default function TargetEditor(
  props: {
    target: any,
    setTarget: (scan: any) => void,
    showCalibrator: boolean
}) {
  const [validation, setValidation] = React.useState<any>({
    nameHelperText: '',
    isNameValid: false,
    isValid: false,
    isPlanet: false,
    isRAValid: false,
    isDecValid: false
  });

  const [isCalibrator, setIsCalibrator] = React.useState(false);
  const [calibratorNames, setCalibratorNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    const newIsValid = Utils.validateTarget(props.target);
    setValidation(newIsValid);
  }, [props.target]);

  React.useEffect(() => {
    const params = new URLSearchParams({
      'action': 'names'
    });  

    fetch(`${ATCAConstants.CALIBRATOR_SEARCH_URL}?${params}`)
      .then(res => res.json())
      .then((res: any) => {
        const names = (res||{})['names'] || [];
        setCalibratorNames(names);
      })
      .catch(error => {
        // could not get calibrator
      });
  }, [isCalibrator]);

  const setCalibratorValue = (value: string) => {
    if (calibratorNames.includes(value)) {
      const formData = new FormData();
      formData.append("action", 'info');
      formData.append("source", value);
  
      fetch(`${ATCAConstants.CALIBRATOR_SEARCH_URL}`, 
        {
          method: 'POST',
          body: formData
        })
        .then(res => res.json())
        .then((res: any) => {
          props.setTarget({
            name: value,
            isCalibrator: true,
            coord_sys: 'J2000',
            ra: res['rightascension'] || '',
            dec: res['declination'] || '',
            velocity: '',
            frame: '',
            convention: ''
          });
        })
        .catch(error => {
          // could not get calibrator
        });  
    } else {
      props.setTarget({
        name: value,
        coord_sys: 'J2000',
        ra: '',
        dec: '',
        velocity: '',
        frame: '',
        convention: ''
      });
    }
  }

  const setTargetValue = (fieldName: string, value: any) => {
    // check if a planet
    if (fieldName === 'name') {
      if (Planets.getPlanetNames().includes(value)) {
        props.setTarget({
          name: value,
          coord_sys: 'J2000',
          ra: '',
          dec: '',
          velocity: '',
          frame: '',
          convention: ''
        });
        return;
      }
    }
    props.setTarget({
      ...props.target,
      [fieldName]: value
    });
  }

  return (
    <Stack spacing={2} width={'100%'}>
      <Stack spacing={2} direction={'row'}>
        {
          isCalibrator ?
          <Autocomplete
            disablePortal
            sx={{flexGrow: 4, height: '75px'}}
            value={(props.target||{})['name'] || ''}
            onChange={(event: any, newValue: string | null) => setCalibratorValue(newValue||'')}
            options={calibratorNames}
            renderInput={(params) => <TextField 
              {...params} label="Name" variant="standard"/>}
          />
          :        
          <TextField id="default-duration"
            sx={{flexGrow: 4, height: '75px'}}
            helperText={validation['nameHelperText']}
            error={!validation['isNameValid']}
            value={(props.target||{})['name'] || ''}
            onChange={(e) => setTargetValue('name', e.target.value)}
            label="Name" variant="standard" 
          />
        }
        {
          props.showCalibrator &&
          <FormControlLabel control={
            <Switch
              checked={isCalibrator}
              onChange={(event) => setIsCalibrator(event.target.checked)}
              inputProps={{ 'aria-label': 'controlled' }}
            />} 
            label="Calibrator" 
          />
        }

      </Stack>


      <FormControl variant="standard" fullWidth 
        disabled={validation['isPlanet'] || isCalibrator}>
        <InputLabel id="scan-coord-sys-label">Coordinate system</InputLabel>
        <Select
          labelId="scan-coord-sys-label"
          id="scan-ecoord-syspoch"
          label="Coordinate system"
          displayEmpty={true}
          value={(props.target||{})['coord_sys'] || 'J2000'}
          onChange={(e) => setTargetValue('coord_sys', e.target.value as string)}
        >
          {ATCAConstants.COORDINATE_SYSTEMS.map((val) => (
            <MenuItem key={val} value={val}>{val}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction={'row'} spacing={2}>
        <TextField id="default-ra" variant="standard" sx={{ flexGrow: '4' }}
          label={ATCAConstants.COORDINATE_SYSTEM_FIELDS[(props.target||{})['coord_sys']||'J2000']['ra']}
          helperText={ATCAConstants.COORDINATE_SYSTEM_FIELDS[(props.target||{})['coord_sys']||'J2000']['ra_format']}
          onChange={(e) => setTargetValue('ra', e.target.value)}
          error={!validation['isRAValid']}
          value={(props.target||{})['ra'] || ''}
          disabled={validation['isPlanet'] || isCalibrator}
        />

        <TextField id="default-dec" variant="standard" sx={{ flexGrow: '4' }}
          label={ATCAConstants.COORDINATE_SYSTEM_FIELDS[(props.target||{})['coord_sys']||'J2000']['dec']}
          helperText={ATCAConstants.COORDINATE_SYSTEM_FIELDS[(props.target||{})['coord_sys']||'J2000']['dec_format']}
          error={!validation['isDecValid']}
          value={(props.target||{})['dec'] || ''}
          onChange={(e) => setTargetValue('dec', e.target.value)}
          disabled={validation['isPlanet'] || isCalibrator}
        />
      </Stack>

      <Stack direction={'row'} spacing={2}>
        <TextField id="velocity" label="Velocity (km/s or redshift)"
          disabled={validation['isPlanet'] || isCalibrator}
          variant="standard"
          sx={{ flexGrow: '4' }}
          value={(props.target||{})['velocity'] || ''}
          onChange={e => setTargetValue('velocity', e.target.value)}
        />

        <FormControl variant="standard" 
          disabled={validation['isPlanet'] || isCalibrator}
          sx={{ minWidth: '125px' }}>
          <InputLabel id="scan-frame-label">Frame</InputLabel>
          <Select
            labelId="scan-frame-label"
            id="scan-frame"
            label="Frame"
            value={(props.target||{})['frame'] || 'lsr'}
            onChange={(e) => setTargetValue('frame', e.target.value as string)}
          >
            <MenuItem value={'lsr'}>LSR</MenuItem>
            <MenuItem value={'barycentric'}>Barycentric</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="standard" 
          disabled={validation['isPlanet'] || isCalibrator}
          sx={{ minWidth: '125px' }}>
          <InputLabel id="scan-convention-label">Convention</InputLabel>
          <Select
            labelId="scan-convention-label"
            id="scan-convention"
            label="Convention"
            value={(props.target||{})['convention'] || 'radio'}
            onChange={(e) => setTargetValue('convention', e.target.value as string)}
          >
            <MenuItem value={'radio'}>Radio</MenuItem>
            <MenuItem value={'optical'}>Optical</MenuItem>
            <MenuItem value={'z'}>Z</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
}